import { AlertOctagon } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { errorMessage } from '@/api/client';
import { Button } from './Button';
import { EmptyState } from './EmptyState';

export interface ErrorFallbackProps {
  error: unknown;
  /** Retry handler; defaults to reloading the page. */
  onRetry?: () => void;
  title?: string;
}

/** Error view used by boundaries and failed queries. */
export function ErrorFallback({ error, onRetry, title = 'Something went wrong' }: ErrorFallbackProps) {
  return (
    <EmptyState
      icon={<AlertOctagon />}
      title={title}
      description={errorMessage(error)}
      action={
        <Button onClick={onRetry ?? (() => window.location.reload())}>
          {onRetry ? 'Try again' : 'Reload page'}
        </Button>
      }
    />
  );
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback; receives the error and a reset function. */
  fallback?: (error: unknown, reset: () => void) => ReactNode;
  /** When any of these change, the boundary resets (e.g. `[location.pathname]`). */
  resetKeys?: readonly unknown[];
}

interface ErrorBoundaryState {
  error: unknown;
  keys: readonly unknown[] | undefined;
}

/** Catches render errors of a subtree (e.g. one annotator panel) and shows a fallback. */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, keys: this.props.resetKeys };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error: error ?? new Error('Unknown error') };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    const changed =
      props.resetKeys?.length !== state.keys?.length ||
      (props.resetKeys ?? []).some((key, i) => !Object.is(key, state.keys?.[i]));
    return changed ? { error: null, keys: props.resetKeys } : null;
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('Render error', error, info.componentStack);
  }

  private readonly reset = () => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return this.props.fallback ? (
      this.props.fallback(error, this.reset)
    ) : (
      <ErrorFallback error={error} onRetry={this.reset} />
    );
  }
}
